import { useCallback, useEffect, useState } from 'react';
import Page from '../../../components/page/Page';
import {
  Button,
  Paper,
  Chip,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useMasterCore } from '../../../../core/modules/master';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import Header from '../../../components/operations/validContainer/Header';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useBusinessCore } from '../../../../core/modules/business';
import {
  DESTINY_CONTAINER_TYPE_COLOR,
  DESTINY_CONTAINER_TYPE_LABEL,
  DESTINY_CONTAINER_TYPES,
} from '../../../../core/constants/containerTypes';
import PrintIcon from '@mui/icons-material/Print';
import { Container } from '../../../../core/interfaces/container.interface';
import ArticleIcon from '@mui/icons-material/Article';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { usePrintLabel } from '../../../../core/hooks/usePrintLabel';
import { useLocationCore } from '../../../../core/modules/location';
import CreateDialog from '../../../components/administration/containers/CreateDialog';
import UploadImagesDialog from '../../../components/operations/validContainer/UploadImagesDialog';

const ValidContainer = () => {
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const {
    containers,
    noMore,
    options,
    getContainers,
    putContainer,
    increasePage,
    setContainer,
    clearContainer,
  } = useContainerCore();
  const { getCategories, getTypes, getPriceRanges } = useMasterCore();
  const { goToWelcome, goToContainerProducts } = useNavigationCore();
  const { getBusinesses } = useBusinessCore();
  const { printDestinationContainer } = usePrintLabel();
  const { locations } = useLocationCore();

  const [editingStatus, setEditingStatus] = useState<Container | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [uploadImagesContainer, setUploadImagesContainer] = useState<Container | null>(null);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (!user || !user.privileges.superAdmin) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    clearContainer();
    if (companyId) {
      getTypes(companyId, 'container');
      getCategories(companyId, 'container');
      getPriceRanges(companyId);
    }
  }, [
    clearContainer,
    companyId,
    getBusinesses,
    getCategories,
    getPriceRanges,
    getTypes,
  ]);

  useEffect(() => {
    if (companyId) {
      // Resetear el estado de carga cuando cambien los filtros
      setHasLoaded(false);
      getContainers(companyId, options, () => {
        // onSuccess callback - cuando se cargan los contenedores
        setHasLoaded(true);
      }, () => {
        // onFailure callback - también marcamos como cargado para mostrar el mensaje de error o vacío
        setHasLoaded(true);
      });
    }
  }, [getContainers, companyId, options]);

  // Función utilitaria para validar y formatear fechas
  const formatDate = (dateString: string | number | null | undefined): string => {
    if (!dateString) return 'N/A';
    
    // El backend envía Unix timestamp en segundos, JavaScript necesita milisegundos
    const timestamp = typeof dateString === 'number' ? dateString * 1000 : dateString;
    const date = new Date(timestamp);
    
    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const updateContainerStatus = useCallback(
    (container: Container, status: number) => {
      if (companyId && status) {
        putContainer(
          companyId,
          container.id,
          { 
            container_status_id: status,
            description: container.description,
            container_category_id: container.container_category_id,
            container_recipient_type_id: container.container_recipient_type_id ?? 0,
            channel_type_id: container.channel_type_id,
            price_range_id: container.price_range_id,
            location_id: container.location_id || undefined,
          },
          () => {
            getContainers(companyId, options);
            setEditingStatus(null);
          }
        );
      }
    },
    [companyId, options, putContainer, getContainers]
  );

  const seeProducts = useCallback(
    (container: Container) => {
      // Limpiar el contenedor anterior antes de establecer el nuevo
      clearContainer();
      setContainer(container);
      goToContainerProducts(container.id);
    },
    [goToContainerProducts, setContainer, clearContainer]
  );

  const print = useCallback(
    (ev: any, container: Container) => {
      ev.stopPropagation();
      printDestinationContainer(container);
    },
    [printDestinationContainer]
  );

  const openUploadImages = useCallback(
    (container: Container) => {
      setUploadImagesContainer(container);
    },
    []
  );

  const handleUploadSuccess = useCallback(() => {
    if (companyId) {
      getContainers(companyId, options);
    }
  }, [companyId, options, getContainers]);

  return (
    <>
      <Page
        header={<Header />}
        content={
          !hasLoaded ? (
            <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
              <CircularProgress />
            </div>
          ) : !containers.length ? (
            <div className="flex flex-1 items-center justify-center h-full" style={{ minHeight: '400px' }}>
              <Typography color="text.secondary" variant="h5">
                No hay contenedores vigentes
              </Typography>
            </div>
          ) : (
            <div className="w-full my-16 sm:col-span-3 px-24 md:px-32">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-24 w-full min-w-0">
                {containers.map((container) => (
                  <Paper
                    className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden py-12"
                    key={container.id}
                  >
                    <div className="flex flex-col flex-auto w-full p-12 items-center text-center">
                      <InventoryIcon className="text-9xl text-gray-600" />

                      <div className="mb-12 flex gap-2">
                        {editingStatus?.id !== container.id && (
                          <Chip
                            size="small"
                            label={
                              DESTINY_CONTAINER_TYPE_LABEL[
                                container.container_status_id
                              ]
                            }
                            color={
                              DESTINY_CONTAINER_TYPE_COLOR[
                                container.container_status_id
                              ]
                            }
                            onClick={() => setEditingStatus(container)}
                          />
                        )}
                        {editingStatus && editingStatus.id === container.id && (
                          <Select
                            labelId="container-status-label"
                            size="small"
                            value={container.container_status_id}
                            onChange={(ev) =>
                              updateContainerStatus(container, +ev.target.value)
                            }
                            required
                          >
                            {DESTINY_CONTAINER_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </div>

                      <Typography className="font-medium text-gray-600 mt-8 mb-12">
                        {container.description}
                      </Typography>

                      {/* Código del contenedor */}
                      <div className="mb-12">
                        <Typography className="text-sm text-gray-500 mb-2 text-center">
                          Código del contenedor
                        </Typography>
                        <Typography className="font-bold text-lg text-gray-800">
                          {container.code}
                        </Typography>
                      </div>

                      {/* Ubicación */}
                      {container.location_id && (
                        <div className="mb-12">
                          <Typography className="text-sm text-gray-500 mb-2 text-center">
                            Ubicación
                          </Typography>
                          <Typography className="font-medium text-gray-700">
                            {locations.find(loc => loc.id === container.location_id)?.code || 'Sin ubicación'}
                          </Typography>
                        </div>
                      )}

                      {/* Fecha de creación */}
                      {container.created_at && (
                        <div className="mb-12">
                          <Typography className="text-sm text-gray-500 mb-2 text-center">
                            Creado
                          </Typography>
                          <Typography className="font-medium text-gray-700">
                            {formatDate(container.created_at)}
                          </Typography>
                        </div>
                      )}
                    </div>

                    {/* Botones de acción */}
                    <div className="flex flex-col gap-8 w-full px-12">
                      <div className="flex gap-8">
                        <Button
                          className="flex-1"
                          color="secondary"
                          onClick={(ev) => print(ev, container)}
                          size="small"
                        >
                          <PrintIcon className="mx-2" />
                          Imprimir
                        </Button>
                        <Button
                          className="flex-1"
                          color="success"
                          onClick={() => seeProducts(container)}
                          size="small"
                        >
                          <ArticleIcon className="mx-2" />
                          {`Artículos (${container.articles_count ?? 0})`}
                        </Button>
                      </div>
                      <Button
                        className="w-full"
                        color="primary"
                        variant="outlined"
                        onClick={() => openUploadImages(container)}
                        size="small"
                        startIcon={<AddPhotoAlternateIcon />}
                      >
                        + Imagen
                      </Button>
                    </div>
                  </Paper>
                ))}
              </div>
              {!noMore ? (
                <div
                  className="text-center underline cursor-pointer my-24"
                  onClick={increasePage}
                >
                  Mostras más
                </div>
              ) : null}
            </div>
          )
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UploadImagesDialog
        open={uploadImagesContainer !== null}
        onClose={() => setUploadImagesContainer(null)}
        container={uploadImagesContainer}
        onSuccess={handleUploadSuccess}
      />
    </>
  );
};

export default ValidContainer;
